import { useEffect, useState } from 'react'
import type { MonthlyBudget } from '../../types/family-finance'
import { getBudget } from '../../lib/family-finance-api'
import { Card, EmptyState, LoadError, LoadingRows, ProgressBar, StatCard, errorMessage, money } from './shared'

export default function BudgetTab() {
  const [budget, setBudget] = useState<MonthlyBudget | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoadError(null)
    getBudget()
      .then((b) => {
        if (!cancelled) setBudget(b)
      })
      .catch((err) => {
        if (!cancelled) setLoadError(errorMessage(err))
      })
    return () => {
      cancelled = true
    }
  }, [reloadKey])

  if (loadError) return <LoadError error={loadError} onRetry={() => setReloadKey((k) => k + 1)} />
  if (!budget) return <LoadingRows rows={6} />

  const total = budget.categories.reduce((s, c) => s + c.budgeted, 0)
  const spent = budget.categories.reduce((s, c) => s + c.spent, 0)
  const over = budget.categories.filter((c) => c.spent > c.budgeted)

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard label={`Budgeted — ${budget.month}`} value={money(total)} />
        <StatCard
          label="Spent So Far"
          value={money(spent)}
          sub={total > 0 ? `${Math.round((spent / total) * 100)}% of budget` : 'No budget set yet'}
          subTone={spent > total ? 'red' : 'slate'}
        />
        <StatCard
          label="Remaining"
          value={money(Math.max(0, total - spent))}
          sub={over.length > 0 ? `${over.length} categor${over.length === 1 ? 'y' : 'ies'} over` : 'All categories on track'}
          subTone={over.length > 0 ? 'amber' : 'green'}
        />
      </div>

      <Card title="Categories" subtitle="Spending synced from linked accounts, categorized automatically">
        {budget.categories.length === 0 && (
          <EmptyState
            icon="🧮"
            message="No budget categories yet — they appear automatically once transactions sync from linked accounts."
          />
        )}
        <div className="space-y-4">
          {budget.categories.map((c) => {
            const isOver = c.spent > c.budgeted
            return (
              <div key={c.id}>
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-sm text-slate-800 dark:text-slate-200">
                    <span className="mr-2">{c.icon}</span>
                    {c.name}
                    {isOver && <span className="ml-2 text-xs text-red-600 dark:text-red-400">over by {money(c.spent - c.budgeted)}</span>}
                  </p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    <span className={isOver ? 'text-red-600 dark:text-red-400 font-medium' : 'text-slate-900 dark:text-white font-medium'}>{money(c.spent)}</span>
                    {' / '}
                    {money(c.budgeted)}
                  </p>
                </div>
                <ProgressBar value={c.spent} max={c.budgeted} tone={isOver ? 'red' : 'blue'} />
              </div>
            )
          })}
        </div>
      </Card>
    </div>
  )
}
